// Slider de "barra rellena" para el modo "Video con influencer" (V2).
//
// Layout (ADR-528/529): el TÍTULO va FUERA de la barra — una fila arriba con
// el label (izquierda) y el valor (derecha) — y la barra de abajo es solo el
// slider: un rectángulo con esquinas redondeadas que se llena de azul sólido
// (brand-primary) hasta la posición elegida. Drag/tap con Pointer Events.
// Lo usan "Velocidad de habla" y "Duración" de la sección Voz y guion.

import React from 'react';

export interface FillBarSliderV2Props {
  /** Label arriba de la barra (ej. "Velocidad de habla"). */
  label: string;
  /**
   * `'celda'`: la barra vive DENTRO de una `CeldaAjuste` del pie, que ya pone la
   * etiqueta arriba. Se dibuja sólo la barra —más baja— con el valor al lado, para que
   * la duración se lea igual que Formato o Calidad, que son sus vecinas de fila
   * (ADR-2026-09-04-la-duracion-es-una-celda-del-pie). `'bloque'` es la de siempre:
   * etiqueta y valor arriba, barra de 40px a lo ancho.
   */
  variante?: 'bloque' | 'celda';
  min: number;
  max: number;
  step: number;
  value: number;
  onChange: (v: number) => void;
  /** Render del valor a la derecha del label (ej. `x 1.10`, `18s`). */
  formatValue: (v: number) => string;
}

export const FillBarSliderV2: React.FC<FillBarSliderV2Props> = ({
  label,
  min,
  max,
  step,
  value,
  onChange,
  formatValue,
  variante = 'bloque',
}) => {
  const stepCount = Math.max(1, Math.round((max - min) / step));

  const calcVal = (clientX: number, rect: DOMRect) => {
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const stepIdx = Math.round(ratio * stepCount);
    return Math.round((min + stepIdx * step) * 100) / 100;
  };

  const clamped = Math.min(max, Math.max(min, value));
  const fillPct = ((clamped - min) / (max - min)) * 100;

  // Barra: rectángulo redondeado que se llena de azul hasta el valor. La MISMA en las
  // dos variantes —sólo cambia el alto—, así el arrastre se escribe una vez.
  const barra = (
      <div
        className={`relative rounded-xl bg-white border border-neutral-200 overflow-hidden select-none cursor-pointer ${
          variante === 'celda' ? 'h-6 flex-1 min-w-0' : 'h-10'
        }`}
        style={{ touchAction: 'none' }}
        onPointerDown={(e) => {
          const el = e.currentTarget;
          el.setPointerCapture(e.pointerId);
          const rect = el.getBoundingClientRect();
          onChange(calcVal(e.clientX, rect));
          const onMove = (ev: PointerEvent) =>
            onChange(calcVal(ev.clientX, rect));
          const onUp = () => {
            el.removeEventListener('pointermove', onMove);
            el.removeEventListener('pointerup', onUp);
            el.removeEventListener('pointercancel', onUp);
          };
          el.addEventListener('pointermove', onMove);
          el.addEventListener('pointerup', onUp);
          el.addEventListener('pointercancel', onUp);
        }}
      >
        <div
          className="absolute inset-y-0 left-0 bg-brand-primary pointer-events-none"
          style={{ width: `${fillPct}%` }}
        />
      </div>
  );

  if (variante === 'celda') {
    return (
      <span className="mt-1 flex items-center gap-2">
        {barra}
        <span className="text-[13.5px] font-semibold text-brand-primary tabular-nums whitespace-nowrap">
          {formatValue(clamped)}
        </span>
      </span>
    );
  }

  return (
    <div>
      {/* Título + valor — FUERA de la barra */}
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-[11px] uppercase tracking-wide font-medium text-[#3C3C43]/55">
          {label}
        </p>
        <span className="text-[13px] font-semibold text-brand-primary tabular-nums whitespace-nowrap">
          {formatValue(clamped)}
        </span>
      </div>
      {barra}
    </div>
  );
};
